import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Wand2, Palette, Download, ArrowRight, CheckCircle2, Layers, AlertTriangle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getConcept, scoreTone, rebuildPdfBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RebuildGateDialog from "@/components/RebuildGateDialog";
import { toast } from "sonner";

export default function ConceptPreview() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(undefined);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    // Keep private previews out of search results.
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    getConcept(id).then(setData).catch(() => setData(null));
    return () => { document.head.removeChild(meta); };
  }, [id]);

  const openPdf = async (token) => {
    try {
      const url = await rebuildPdfBlob(id, user ? {} : { token });
      const win = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      if (!win) toast.error("Please allow pop-ups to open the report.");
    } catch {
      toast.error("Could not open the report. Please try again.");
    }
  };

  const onDownload = () => (user ? openPdf() : setGateOpen(true));

  if (data === undefined) {
    return <><Navbar /><div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-brand" size={28} /></div><Footer /></>;
  }
  if (data === null) {
    return <><Navbar /><main className="pt-40 pb-40 text-center min-h-screen"><h1 className="font-heading text-4xl font-bold text-slate-900">Preview not found</h1><Link to="/" className="text-brand mt-4 inline-block">Back home</Link></main><Footer /></>;
  }

  const c = data.concept || {};
  const hero = c.hero || {};
  const tone = scoreTone(data.overall_score ?? 0);

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-slate-50" data-testid="concept-preview">
        {/* Hero */}
        <section className="bg-gradient-to-b from-emerald-50 to-white text-slate-900 pt-32 pb-16 md:pt-40 md:pb-20 border-b border-emerald-100/70">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 size={16} className="text-emerald-700" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Website Transformation Preview · {data.domain}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold max-w-3xl leading-[1.05]">{hero.headline}</h1>
            <p className="text-slate-600 text-base md:text-lg mt-4 max-w-2xl">{hero.subheadline}</p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              {hero.primary_cta && <span className="bg-brand text-white text-sm font-semibold rounded-lg px-5 py-2.5">{hero.primary_cta}</span>}
              <button onClick={onDownload} data-testid="preview-download-pdf"
                className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold rounded-xl px-5 py-3 hover:-translate-y-0.5 transition-transform">
                <Download size={16} /> Download the full report
              </button>
              {data.overall_score != null && (
                <div className="ml-auto text-right">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Opportunity Score</p>
                  <p className="font-heading text-4xl font-extrabold" style={{ color: tone.color }}>{data.overall_score}<span className="text-lg text-slate-400">/100</span></p>
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 space-y-10">
          {data.content_readable === false && (
            <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3.5" data-testid="preview-unreadable-notice">
              <AlertTriangle size={17} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">
                Heads up: we couldn't fully read <span className="font-semibold">{data.domain}</span>
                {data.block_reason === "bot-protection" || data.block_reason === "blocked" ? " (it blocks automated visits)" : data.block_reason === "thin-or-js-rendered" ? " (it's mostly JavaScript-rendered)" : ""}.
                {" "}This concept is based on limited public info and the business name, and the category scores are conservative estimates — for a precise redesign, share a bit about the business or a page we can read.
              </p>
            </div>
          )}
          {/* Style + palette + nav */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6">
              <div className="flex items-center gap-2 mb-3"><Palette size={15} className="text-brand" /><span className="overline">Style & Palette</span></div>
              <p className="text-slate-600 text-sm leading-relaxed">{c.style_direction}</p>
              <div className="flex gap-2 mt-4 flex-wrap">
                {(c.color_palette || []).map((p, i) => (
                  <div key={i} className="text-center">
                    <div className="w-14 h-14 rounded-lg border border-slate-200" style={{ background: p.hex }} />
                    <p className="text-[9px] font-mono text-slate-400 mt-1">{p.hex}</p>
                  </div>
                ))}
              </div>
              {(c.navigation || []).length > 0 && (
                <div className="mt-6">
                  <span className="overline">Recommended Navigation</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {c.navigation.map((n, i) => <span key={i} className="text-xs font-medium bg-slate-100 rounded px-2.5 py-1 text-slate-600">{n}</span>)}
                  </div>
                </div>
              )}
              {(c.recommended_features || []).length > 0 && (
                <div className="mt-6">
                  <span className="overline">Recommended Features</span>
                  <div className="mt-2 space-y-1.5">
                    {c.recommended_features.map((f, i) => (
                      <p key={i} className="text-sm text-slate-600 flex items-start gap-2"><CheckCircle2 size={15} className="text-brand mt-0.5 shrink-0" /> {f}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6">
              <div className="flex items-center gap-2 mb-3"><Layers size={15} className="text-brand" /><span className="overline">Proposed Sections</span></div>
              <div className="space-y-2">
                {(c.sections || []).map((s, i) => (
                  <div key={i} className="border border-slate-100 rounded-lg p-3">
                    <p className="text-sm font-semibold text-slate-800">{s.title}</p>
                    <p className="text-xs text-slate-500">{s.purpose}</p>
                  </div>
                ))}
              </div>
              {(c.cta_strategy || []).length > 0 && (
                <div className="mt-6">
                  <span className="overline">Call-to-Action Strategy</span>
                  <div className="mt-2 space-y-1.5">
                    {c.cta_strategy.map((s, i) => <p key={i} className="text-sm text-slate-600 flex items-start gap-2"><ArrowRight size={14} className="text-brand mt-1 shrink-0" /> {s}</p>)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Before / after */}
          {(c.before_after || []).length > 0 && (
            <div>
              <span className="overline">Before &amp; Proposed</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                {c.before_after.map((b, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 2) * 0.06 }}
                    className="bg-white border border-slate-200 rounded-2xl card-shadow p-5">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{b.area}</p>
                    <p className="text-sm text-slate-500 mt-2"><span className="font-semibold text-slate-600">Now:</span> {b.before}</p>
                    <p className="text-sm text-slate-700 mt-2"><span className="font-semibold text-brand">Proposed:</span> {b.after}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-slate-400 text-center max-w-2xl mx-auto">
            Conceptual redesign prepared from publicly available website information. Final design, content and functionality are subject to owner review. Nothing has been published or changed on your live website.
          </p>

          {/* CTA */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Like what you see for {data.domain}?</h2>
            <p className="text-slate-600 mt-2 max-w-xl mx-auto">Request changes, get an estimate, or book a free consultation — we'll turn this concept into a live prototype.</p>
            <Link to="/#analyzer" data-testid="preview-cta" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold rounded-xl px-6 py-3.5 mt-6 hover:-translate-y-0.5 transition-transform">
              Book a free consultation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </main>
      <RebuildGateDialog
        open={gateOpen}
        onOpenChange={setGateOpen}
        conceptId={id}
        domain={data.domain}
        onUnlocked={({ token }) => openPdf(token)}
      />
      <Footer />
    </>
  );
}
